<template>
  <section class="store-detail sd">
    <v-progress-linear v-if="loading" indeterminate class="sd-loading" />

    <!-- ================= HERO ================= -->
    <header class="sd-hero" :style="heroStyle">
      <div class="sd-hero__scrim" />
      <v-btn
        icon
        small
        dark
        class="sd-back"
        @click="$router.push('/administrator/stores')"
      >
        <v-icon small>fal fa-arrow-left</v-icon>
      </v-btn>

      <div class="sd-hero__inner">
        <button
          type="button"
          class="sd-logo"
          :disabled="!store.logo"
          @click="openLogo"
        >
          <img v-if="store.logo" :src="logoUrl" :alt="store.name" />
          <v-icon v-else>fal fa-store</v-icon>
        </button>

        <div class="sd-hero__id">
          <div class="sd-chips">
            <span class="sd-chip">ID {{ store.id }}</span>
            <span v-if="store.code" class="sd-chip">{{ store.code }}</span>
            <span v-if="store.store_type_code" class="sd-chip">{{
              store.store_type_code
            }}</span>
            <span
              class="sd-chip"
              :class="store.is_active ? 'sd-chip--ok' : 'sd-chip--bad'"
            >
              {{ store.is_active ? $t("active") : $t("inactive") }}
            </span>
          </div>
          <h1>{{ store.name || "-" }}</h1>
          <p v-if="store.desc">{{ store.desc }}</p>
        </div>

        <div class="sd-ring" :class="`sd-ring--${ringTone}`">
          <svg viewBox="0 0 100 100" aria-hidden="true">
            <circle class="sd-ring__track" cx="50" cy="50" r="42" />
            <circle
              class="sd-ring__bar"
              cx="50"
              cy="50"
              r="42"
              :stroke-dasharray="ringDash"
            />
          </svg>
          <div class="sd-ring__label">
            <strong>{{ ringValue }}</strong>
            <span>{{ ringCaption }}</span>
          </div>
        </div>
      </div>
    </header>

    <div class="sd-grid">
      <!-- ================= MAIN COLUMN ================= -->
      <div class="sd-main">
        <article class="sd-card">
          <div class="sd-card__head">
            <h2>{{ $t("store_subscription") }}</h2>
            <v-chip v-if="storeExpired" x-small label outlined color="error">
              <v-icon x-small left>fal fa-ban</v-icon
              >{{ $t("branch_status_expired") }}
            </v-chip>
          </div>
          <div class="sd-plan">
            <span class="sd-plan__label">{{ $t("package") }}</span>
            <strong>{{ store.package ? store.package.name : "-" }}</strong>
          </div>
          <div class="sd-timeline">
            <div class="sd-track">
              <span
                class="sd-fill"
                :class="`sd-bar--${ringTone}`"
                :style="{ width: periodPct + '%' }"
              />
              <span
                v-if="hasPeriod"
                class="sd-timeline__now"
                :style="{ left: periodPct + '%' }"
              />
            </div>
            <div class="sd-timeline__dates">
              <div>
                <small>{{ $t("start_date") }}</small>
                <b>{{ store.start_date || "-" }}</b>
              </div>
              <div class="sd-right">
                <small>{{ $t("end_date") }}</small>
                <b>{{ store.end_date || "-" }}</b>
              </div>
            </div>
          </div>
        </article>

        <article class="sd-card">
          <div class="sd-card__head">
            <h2>{{ $t("plan_usage") }}</h2>
          </div>
          <div class="sd-meters">
            <div v-for="m in meters" :key="m.key" class="sd-meter">
              <div class="sd-meter__row">
                <span class="sd-meter__label">
                  <v-icon x-small>{{ m.icon }}</v-icon>
                  {{ $t(m.label) }}
                </span>
                <b>
                  {{ m.used | NumberFormat }}
                  <em v-if="m.limit">/ {{ m.limit | NumberFormat }}</em>
                </b>
              </div>
              <div class="sd-track sd-track--thin">
                <span
                  class="sd-fill"
                  :class="`sd-bar--${m.tone}`"
                  :style="{ width: m.pct + '%' }"
                />
              </div>
            </div>
          </div>
        </article>

        <KpiCards class="sd-kpis" :items="kpiItems" />

        <article class="sd-card sd-card--table">
          <div class="sd-card__head">
            <h2>{{ $t("branch") }}</h2>
            <span class="sd-count">{{ branches.length }}</span>
          </div>
          <v-data-table
            :headers="branchHeaders"
            :items="branches"
            :loading="loading"
            hide-default-footer
            disable-pagination
            dense
          >
            <template v-slot:item.start_date="{ item }">{{
              item.start_date || "-"
            }}</template>
            <template v-slot:item.end_date="{ item }">
              {{ endDateLabel(item) }}
              <branch-status-chip :branch="item" class="ml-1" />
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn x-small depressed @click="openDates(item)">
                <v-icon x-small left>fal fa-calendar-alt</v-icon
                >{{ $t("edit_branch_dates") }}
              </v-btn>
            </template>
          </v-data-table>
        </article>
      </div>

      <!-- ================= SIDE COLUMN ================= -->
      <aside class="sd-side">
        <article class="sd-card">
          <div class="sd-card__head">
            <h2>{{ $t("store_contact") }}</h2>
          </div>
          <div class="sd-owner">
            <span class="sd-owner__avatar">{{ ownerInitials }}</span>
            <div class="sd-owner__text">
              <small>{{ $t("owner") }}</small>
              <strong>{{ ownerName }}</strong>
              <span v-if="owner && owner.email" class="sd-muted">{{
                owner.email
              }}</span>
            </div>
          </div>
          <ul class="sd-contact">
            <li v-if="ownerPhone">
              <v-icon x-small>fal fa-user</v-icon>
              <a :href="`tel:${ownerPhone}`">{{ ownerPhone }}</a>
            </li>
            <li v-if="store.phone_no">
              <v-icon x-small>fal fa-phone</v-icon>
              <a :href="`tel:${store.phone_no}`">{{ store.phone_no }}</a>
            </li>
            <li v-if="store.mobile_no">
              <v-icon x-small>fal fa-mobile</v-icon>
              <a :href="`tel:${store.mobile_no}`">{{ store.mobile_no }}</a>
            </li>
            <li v-if="store.address">
              <v-icon x-small>fal fa-map-marker-alt</v-icon>
              <span>{{ store.address }}</span>
            </li>
          </ul>
          <v-btn
            small
            depressed
            block
            color="green"
            dark
            class="sd-whatsapp"
            @click="sendWhatsapp(store)"
          >
            <v-icon x-small left>fab fa-whatsapp</v-icon>{{ $t("send_notify") }}
          </v-btn>
        </article>

        <article class="sd-card">
          <div class="sd-card__head">
            <h2>{{ $t("join_online_status") }}</h2>
            <span class="sd-pill" :class="`sd-pill--${joinTone}`">{{
              store.join ? $t(store.join) : "-"
            }}</span>
          </div>
          <div class="sd-actions">
            <template v-if="store.join === 'processing'">
              <v-btn small depressed color="primary" @click="joinSubmit('approved', 'is_approved_purchase')">
                <v-icon x-small left>fal fa-check-circle</v-icon>{{ $t("approved") }}
              </v-btn>
              <v-btn small depressed color="red" dark @click="joinSubmit('decline', 'rejected_online_request')">
                <v-icon x-small left>fal fa-times-circle</v-icon>{{ $t("rejected") }}
              </v-btn>
            </template>
            <v-btn v-if="store.join === 'approved'" small depressed color="red" dark @click="joinSubmit('cancel', 'cancel_online_request')">
              <v-icon x-small left>fal fa-times-circle</v-icon>{{ $t("cancel") }}
            </v-btn>
            <v-btn v-if="store.join === 'pending'" small depressed color="primary" @click="joinSubmit('pending', 'join_e_commerce')">
              <v-icon x-small left>fal fa-plus-circle</v-icon>{{ $t("join_online") }}
            </v-btn>
          </div>
          <div class="sd-features">
            <span
              v-for="f in features"
              :key="f.key"
              class="sd-feature"
              :class="{ 'sd-feature--on': f.on }"
            >
              <v-icon x-small>{{ f.on ? "fal fa-check" : "fal fa-minus" }}</v-icon>
              {{ $t(f.key) }}
            </span>
          </div>
        </article>

        <article class="sd-card">
          <div class="sd-card__head">
            <h2>{{ $t("quick_links") }}</h2>
          </div>
          <div class="sd-links">
            <router-link v-for="l in links" :key="l.to" :to="l.to" class="sd-link">
              <span class="sd-link__icon"><v-icon small>{{ l.icon }}</v-icon></span>
              <span>{{ $t(l.label) }}</span>
              <v-icon x-small class="sd-link__arrow">fal fa-chevron-right</v-icon>
            </router-link>
          </div>
        </article>
      </aside>
    </div>

    <app-custom-dialog
      v-model="datesDialog"
      :title="$t('edit_branch_dates')"
      :subtitle="editing.name"
      :is_translate="false"
      title-icon="fal fa-calendar-alt"
      width="420px"
      submit-text="save"
      :submit-disabled="!datesValid"
      :submit-loading="saving"
      @submit="saveDates"
    >
      <div slot="activator">
        <DatePicker
          v-model="editing.start_date"
          label="start_date"
          dense
          clearable
        />
        <DatePicker
          v-model="editing.end_date"
          class="mt-3"
          label="end_date"
          dense
          clearable
          :min-date="editing.start_date || undefined"
          :error-messages="datesValid ? [] : [`${$t('end_date')} < ${$t('start_date')}`]"
        />
      </div>
    </app-custom-dialog>
  </section>
</template>

<script>
import moment from "moment";
import { myMixin } from "@/mixin/main";
import { success, toast, confirm } from "@/controllers/alert";
import { branchEndDateLabel } from "@/utils/branchStatus";
import BranchStatusChip from "@/components/BranchStatusChip.vue";

export default {
  name: "AdminStoreDetail",
  components: { BranchStatusChip },
  mixins: [myMixin],
  data: () => ({
    store: {},
    branches: [],
    loading: false,
    datesDialog: false,
    saving: false,
    editing: { id: null, name: "", start_date: null, end_date: null },
  }),
  computed: {
    storeId() {
      return this.$route.params.store_id;
    },
    logoUrl() {
      return Help.product(this.store.logo);
    },
    daysLeft() {
      if (!this.store.end_date) return null;
      return moment(this.store.end_date, "YYYY-MM-DD").diff(moment().startOf("day"), "days");
    },
    storeExpired() {
      return this.daysLeft !== null && this.daysLeft < 0;
    },
    // ---- subscription ----
    hasPeriod() {
      return !!(this.store.start_date && this.store.end_date);
    },
    periodPct() {
      if (!this.hasPeriod) return 0;
      const start = moment(this.store.start_date, "YYYY-MM-DD");
      const end = moment(this.store.end_date, "YYYY-MM-DD");
      const total = end.diff(start, "days");
      if (total <= 0) return 100;
      const elapsed = moment().startOf("day").diff(start, "days");
      return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
    },
    ringPct() {
      if (this.daysLeft === null) return 100;
      if (this.daysLeft <= 0) return 0;
      if (this.hasPeriod) return 100 - this.periodPct;
      return Math.min(100, (this.daysLeft / 365) * 100);
    },
    ringDash() {
      const c = 2 * Math.PI * 42;
      return `${(this.ringPct / 100) * c} ${c}`;
    },
    ringTone() {
      if (this.daysLeft === null) return "ok";
      if (this.daysLeft <= 7) return "bad";
      if (this.daysLeft <= 30) return "warn";
      return "ok";
    },
    ringValue() {
      return this.daysLeft === null ? "∞" : Math.abs(this.daysLeft);
    },
    ringCaption() {
      return this.storeExpired ? this.$t("expired") : this.$t("days_left");
    },
    heroStyle() {
      return this.store.cover
        ? { backgroundImage: `url(${Help.product(this.store.cover)})` }
        : {};
    },
    // ---- plan usage: how much of each package limit the store has used ----
    meters() {
      const mk = (key, icon, label, used, limit) => {
        used = Number(used || 0);
        limit = Number(limit || 0);
        const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
        const tone = limit <= 0 ? "ok" : pct >= 90 ? "bad" : pct >= 70 ? "warn" : "ok";
        return { key, icon, label, used, limit, pct, tone };
      };
      const s = this.store;
      return [
        mk("products", "fal fa-box", "products", s.products_count, s.product_count),
        mk("users", "fal fa-users", "users", s.stores_user_count, s.user_count),
        mk("branches", "fal fa-store-alt", "branches_count", s.branches_count, s.branch_count),
      ];
    },
    kpiItems() {
      const s = this.store;
      return [
        { key: "customers", title: "customers", icon: "fal fa-user-friends", value: Number(s.customers_count || 0) },
        { key: "categories", title: "categories", icon: "fal fa-tags", value: Number(s.categories_count || 0) },
        { key: "tickets", title: "tickets_count", icon: "fal fa-receipt", value: Number(s.tickets_count || 0) },
      ];
    },
    // ---- contact ----
    owner() {
      return this.store.owner || null;
    },
    ownerName() {
      const o = this.owner;
      if (!o) return "-";
      return [o.first_name, o.last_name].filter(Boolean).join(" ").trim() || o.name || "-";
    },
    ownerInitials() {
      const n = this.ownerName === "-" ? (this.store.name || "?") : this.ownerName;
      return n
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },
    ownerPhone() {
      const o = this.owner;
      return o ? o.phone_no || o.mobile_no || "" : "";
    },
    // ---- online shop ----
    joinTone() {
      return (
        { processing: "warn", approved: "ok", decline: "bad", cancel: "bad" }[this.store.join] ||
        "muted"
      );
    },
    features() {
      const on = (v) => [1, "1", true, "true"].includes(v);
      return ["online_order", "kitchen", "smart_order"].map((key) => ({
        key,
        on: on(this.store[key]),
      }));
    },
    links() {
      const id = this.storeId;
      return [
        { to: `/administrator/store-ticket/${id}`, label: "tickets_count", icon: "fal fa-receipt" },
        { to: `/store-products/${id}`, label: "products", icon: "fal fa-box" },
        { to: `/store-users/${id}`, label: "users", icon: "fal fa-users" },
        { to: `/store-categories/${id}`, label: "categories", icon: "fal fa-tags" },
        { to: `/store-customers/${id}`, label: "customers", icon: "fal fa-user-friends" },
      ];
    },
    branchHeaders() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("address"), value: "address", sortable: false },
        { text: this.$t("start_date"), value: "start_date" },
        { text: this.$t("end_date"), value: "end_date" },
        { text: this.$t("manage"), value: "actions", sortable: false, align: "right" },
      ];
    },
    datesValid() {
      const { start_date, end_date } = this.editing;
      if (!start_date || !end_date) return true;
      return end_date >= start_date;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    endDateLabel(item) {
      return branchEndDateLabel(item, this.$t.bind(this));
    },
    async load() {
      this.loading = true;
      try {
        const [storeRes, branchRes] = await Promise.all([
          this.$http.get(`admin_get_store/${this.storeId}`),
          this.$http.get(`get_branch/${this.storeId}?per_page=50`),
        ]);
        if (storeRes.data.status) this.store = storeRes.data.data || {};
        if (branchRes.data.status) {
          const d = branchRes.data.data;
          this.branches = Array.isArray(d) ? d : (d && d.data) || [];
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    openLogo() {
      window.open(this.logoUrl, "_blank");
    },
    openDates(item) {
      this.editing = {
        id: item.id,
        name: item.name,
        start_date: item.start_date || null,
        end_date: item.end_date || null,
      };
      this.datesDialog = true;
    },
    async saveDates() {
      if (!this.datesValid) return;
      this.saving = true;
      try {
        const { data } = await this.$http.put(`admin/branch_dates/${this.editing.id}`, {
          start_date: this.editing.start_date || null,
          end_date: this.editing.end_date || null,
        });
        if (data.status) {
          const i = this.branches.findIndex((b) => b.id === data.data.id);
          if (i >= 0) this.branches.splice(i, 1, { ...this.branches[i], ...data.data });
          this.datesDialog = false;
          await toast(this, "update_success");
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    // moved from the stores.vue dialog (req_join_online)
    async joinSubmit(title, text) {
      const { isConfirmed } = await confirm({ props: this, title, text });
      if (!isConfirmed) return;
      try {
        const { data } = await this.$http.post("req_join_online", {
          status: title,
          store_id: this.store.id,
        });
        if (data.status) {
          await success(this, data.message);
          await this.load();
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style scoped>
.sd {
  --sd-brand: #5b39c9;
  --sd-ok: #168b78;
  --sd-warn: #c87528;
  --sd-bad: #d33a3a;
  --sd-ink: #252039;
  --sd-muted: #858a9b;
  --sd-line: #e2e8f0;
  max-width: 1320px;
  margin: 0 auto;
  color: var(--sd-ink);
}
.sd-loading {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
}

/* ---------- Hero ---------- */
.sd-hero {
  position: relative;
  overflow: hidden;
  min-height: 190px;
  border-radius: 20px;
  background-color: #2a1a63;
  background-image:
    linear-gradient(135deg, #2a1a63 0%, #5b39c9 100%),
    radial-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1.5px);
  background-position: center, 0 0;
  background-size: cover, 18px 18px;
  color: #fff;
}
/* a real cover image replaces the gradient; the scrim keeps text legible */
.sd-hero[style] {
  background-size: cover, cover;
}
.sd-hero__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(20, 12, 52, 0.88) 0%, rgba(20, 12, 52, 0.55) 60%, rgba(20, 12, 52, 0.3) 100%);
}
.sd-back {
  position: absolute !important;
  top: 12px;
  left: 12px;
  z-index: 1;
  background: rgba(255, 255, 255, 0.14) !important;
}
.sd-hero__inner {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 20px;
  min-height: 190px;
  padding: 24px 28px;
}
.sd-logo {
  display: flex;
  width: 84px;
  height: 84px;
  flex: 0 0 84px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 22px;
  background: #fff;
  color: #9a8cf0;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
}
.sd-logo:not(:disabled) {
  cursor: pointer;
}
.sd-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sd-hero__id {
  min-width: 0;
  flex: 1;
}
.sd-hero__id h1 {
  margin: 8px 0 0;
  overflow: hidden;
  font-size: clamp(24px, 3.4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  text-overflow: ellipsis;
}
.sd-hero__id p {
  display: -webkit-box;
  max-width: 560px;
  margin: 6px 0 0;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.78);
  font-size: 13px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.sd-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sd-chip {
  padding: 3px 9px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  backdrop-filter: blur(4px);
}
.sd-chip--ok {
  border-color: rgba(110, 231, 183, 0.6);
  background: rgba(16, 185, 129, 0.28);
}
.sd-chip--bad {
  border-color: rgba(252, 165, 165, 0.7);
  background: rgba(239, 68, 68, 0.35);
}

/* days-left ring */
.sd-ring {
  position: relative;
  width: 104px;
  height: 104px;
  flex: 0 0 104px;
  border-radius: 50%;
  background: rgba(20, 12, 52, 0.55);
  backdrop-filter: blur(6px);
}
.sd-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
.sd-ring circle {
  fill: none;
  stroke-width: 7;
}
.sd-ring__track {
  stroke: rgba(255, 255, 255, 0.16);
}
.sd-ring__bar {
  stroke: #6ee7b7;
  stroke-linecap: round;
  transition: stroke-dasharray 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.sd-ring--warn .sd-ring__bar {
  stroke: #fbbf24;
}
.sd-ring--bad .sd-ring__bar {
  stroke: #fca5a5;
}
.sd-ring__label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.1;
}
.sd-ring__label strong {
  font-size: 28px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.sd-ring__label span {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* ---------- Layout ---------- */
.sd-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  margin-top: 16px;
}
.sd-main,
.sd-side {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
}

/* ---------- Cards ---------- */
.sd-card {
  padding: 20px 22px;
  border: 1px solid var(--sd-line);
  border-radius: 16px;
  background: #fff;
  box-shadow: none;
}
.sd-card--table {
  padding-right: 0;
  padding-left: 0;
}
.sd-card--table .sd-card__head {
  padding: 0 22px;
}
.sd-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.sd-card__head h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.sd-card__head h2::before {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--sd-brand);
  content: "";
}
.sd-count {
  min-width: 30px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #edeafb;
  color: #6451ba;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
}
.sd-muted {
  color: var(--sd-muted);
  font-size: 12px;
}
.sd-right {
  text-align: right;
}

/* ---------- Subscription ---------- */
.sd-plan {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
}
.sd-plan__label,
.sd-timeline__dates small,
.sd-owner__text small {
  color: var(--sd-muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sd-plan strong {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.sd-track {
  position: relative;
  height: 10px;
  border-radius: 999px;
  background: #eef0f6;
}
.sd-track--thin {
  height: 7px;
}
.sd-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--sd-ok);
  transition: width 0.7s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.sd-bar--warn {
  background: var(--sd-warn);
}
.sd-bar--bad {
  background: var(--sd-bad);
}
.sd-timeline__now {
  position: absolute;
  top: -4px;
  width: 3px;
  height: 18px;
  margin-left: -1.5px;
  border-radius: 2px;
  background: var(--sd-ink);
}
.sd-timeline__dates {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}
.sd-timeline__dates b {
  display: block;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

/* ---------- Plan usage ---------- */
.sd-meters {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}
.sd-meter__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.sd-meter__label {
  color: var(--sd-muted);
  font-size: 12px;
  font-weight: 700;
}
.sd-meter__row b {
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}
.sd-meter__row em {
  color: var(--sd-muted);
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
}

/* ---------- Contact ---------- */
.sd-owner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px dashed #e3e0ee;
}
.sd-owner__avatar {
  display: inline-flex;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #edeafb;
  color: var(--sd-brand);
  font-size: 15px;
  font-weight: 800;
}
.sd-owner__text {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.sd-owner__text strong {
  overflow: hidden;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-contact {
  margin: 12px 0 14px;
  padding: 0;
  list-style: none;
}
.sd-contact li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  line-height: 1.4;
}
.sd-contact .v-icon {
  margin-top: 3px;
  color: var(--sd-brand);
}
.sd-contact a {
  color: inherit;
  text-decoration: none;
  font-variant-numeric: tabular-nums;
}
.sd-contact a:hover {
  color: var(--sd-brand);
}
.sd-whatsapp {
  border-radius: 10px;
  text-transform: none;
}

/* ---------- Online shop ---------- */
.sd-pill {
  padding: 3px 10px;
  border-radius: 999px;
  background: #eef0f6;
  color: #5b6178;
  font-size: 11px;
  font-weight: 800;
  text-transform: capitalize;
}
.sd-pill--ok {
  background: #e3f6ef;
  color: #0f7a64;
}
.sd-pill--warn {
  background: #fff1dc;
  color: #a4611b;
}
.sd-pill--bad {
  background: #fde7e7;
  color: #b42323;
}
.sd-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}
.sd-actions:not(:empty) {
  margin-bottom: 14px;
}
.sd-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sd-feature {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid var(--sd-line);
  border-radius: 8px;
  color: #a2a6b5;
  font-size: 12px;
  font-weight: 600;
}
.sd-feature--on {
  border-color: #bfe7da;
  background: #effaf6;
  color: #0f7a64;
}

/* ---------- Quick links ---------- */
.sd-links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sd-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 12px;
  color: var(--sd-ink);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease;
}
.sd-link:hover {
  background: #f4f2fd;
}
.sd-link__icon {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edeafb;
  color: var(--sd-brand);
}
.sd-link__arrow {
  margin-left: auto;
  color: #b9bccb;
  transition: transform 0.15s ease;
}
.sd-link:hover .sd-link__arrow {
  transform: translateX(3px);
}

/* ---------- Responsive ---------- */
@media (max-width: 1263px) {
  .sd-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 599px) {
  .sd-hero__inner {
    flex-wrap: wrap;
    padding: 56px 18px 18px;
  }
  .sd-ring {
    width: 88px;
    height: 88px;
    flex-basis: 88px;
  }
  .sd-meters {
    grid-template-columns: minmax(0, 1fr);
  }
  .sd-card {
    padding: 16px;
  }
  .sd-card--table .sd-card__head {
    padding: 0 16px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .sd-fill,
  .sd-ring__bar,
  .sd-link__arrow {
    transition: none;
  }
}

/* ---------- Dark mode ---------- */
.theme--dark .sd {
  --sd-ink: #f1f5f9;
  --sd-muted: #94a3b8;
  --sd-line: rgba(255, 255, 255, 0.08);
}
.theme--dark .sd-card {
  background: #1e1e2d;
  box-shadow: none;
}
.theme--dark .sd-track {
  background: rgba(255, 255, 255, 0.08);
}
.theme--dark .sd-count,
.theme--dark .sd-owner__avatar,
.theme--dark .sd-link__icon {
  background: rgba(129, 140, 248, 0.16);
  color: #c4b5fd;
}
.theme--dark .sd-owner {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .sd-link:hover {
  background: rgba(255, 255, 255, 0.05);
}
.theme--dark .sd-timeline__now {
  background: #fff;
}
.theme--dark .sd-pill {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}
.theme--dark .sd-pill--ok,
.theme--dark .sd-feature--on {
  border-color: rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.14);
  color: #6ee7b7;
}
.theme--dark .sd-pill--warn {
  background: rgba(245, 158, 11, 0.16);
  color: #fbbf24;
}
.theme--dark .sd-pill--bad {
  background: rgba(239, 68, 68, 0.16);
  color: #fca5a5;
}
.theme--dark .sd-feature {
  color: #64748b;
}
</style>
